import { useCallback, useEffect, useId, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { colors as themeColors } from 'src/theme';
import { InputLabel } from 'src/components';
import { ValidationErrorStyle } from 'src/common';

const TRACK_HEIGHT = 4;
const FILL_HEIGHT = 6;
const THUMB_SIZE = 20;
const TRACK_RADIUS = 2;
const FILL_RADIUS = 3;

const SliderWrapper = styled.div({
  position: 'relative',
  width: '100%',
  padding: '0 10px'
});

const LabelRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const InputLabelStyle = styled(InputLabel)({
});

const MinMaxRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const MinMaxLabel = styled.span({
  fontSize: 14,
  lineHeight: '20px',
  color: themeColors.text4,
  fontWeight: 400,
});

const SliderRow = styled.div(() => ({
  position: 'relative' as const,
  height: THUMB_SIZE,
  display: 'flex' as const,
  alignItems: 'center' as const,
}));

const HiddenInput = styled.input({
  position: 'absolute',
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  opacity: 0,
  cursor: 'inherit',
  zIndex: 6,
});

const Track = styled.div({
  position: 'absolute',
  left: 0,
  right: 0,
  height: TRACK_HEIGHT,
  borderRadius: TRACK_RADIUS,
  backgroundColor: themeColors.text4,
});

const Fill = styled.div((props: { widthPercent: number }) => ({
  position: 'absolute' as const,
  left: 0,
  width: `${props.widthPercent}%`,
  height: FILL_HEIGHT,
  borderRadius: FILL_RADIUS,
  backgroundColor: themeColors.primary1,
  pointerEvents: 'none' as const,
}));

const Thumb = styled.div((props: { leftPercent: number; disabled?: boolean }) => ({
  position: 'absolute' as const,
  left: `calc(${props.leftPercent}% - ${THUMB_SIZE / 2}px)`,
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  borderRadius: '50%',
  backgroundColor: themeColors.primary1,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  ...(props.disabled && {
    opacity: 0.6,
  }),
}));

export interface SliderInputProps {
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  onChangeCommitted?: (value: number) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export const SliderInput: React.FC<SliderInputProps> = ({
  value: controlledValue,
  min = 0,
  max = 100,
  step = 50,
  onChange,
  onChangeCommitted,
  label,
  error = '',
  disabled = false,
  className,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(() =>
    isControlled ? clamp(controlledValue, min, max) : min
  );
  const value = isControlled ? clamp(controlledValue, min, max) : internalValue;
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const valueToPercent = useCallback(
    (v: number) => (max === min ? 0 : ((v - min) / (max - min)) * 100),
    [min, max]
  );

  const percentToValue = useCallback(
    (p: number) => {
      const clamped = Math.max(0, Math.min(100, p));
      const raw = min + (clamped / 100) * (max - min);
      if (step <= 0) return raw;
      const steps = Math.round((raw - min) / step);
      return clamp(min + steps * step, min, max);
    },
    [min, max, step]
  );

  const updateValue = useCallback(
    (newValue: number) => {
      const clamped = clamp(newValue, min, max);
      if (!isControlled) setInternalValue(clamped);
      onChange?.(clamped);
      if (inputRef.current) {
        inputRef.current.value = String(clamped);
      }
    },
    [min, max, isControlled, onChange]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      if (!Number.isNaN(v)) updateValue(v);
    },
    [updateValue]
  );

  const getValueFromClientX = useCallback(
    (clientX: number) => {
      const el = sliderRef.current;
      if (!el) return value;
      const rect = el.getBoundingClientRect();
      const x = clientX - rect.left;
      const percent = (x / rect.width) * 100;
      return percentToValue(percent);
    },
    [percentToValue, value]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(true);
      updateValue(getValueFromClientX(e.clientX));
    },
    [disabled, getValueFromClientX, updateValue]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return;
      updateValue(getValueFromClientX(e.clientX));
    },
    [isDragging, getValueFromClientX, updateValue]
  );

  const committedValueRef = useRef(value);
  committedValueRef.current = value;

  const handlePointerUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onChangeCommitted?.(committedValueRef.current);
    }
  }, [isDragging, onChangeCommitted]);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDragging, handlePointerMove, handlePointerUp]);

  const percent = valueToPercent(value);

  return (
    <SliderWrapper className={className}>
      {label != null && (
        <LabelRow>
          <InputLabelStyle htmlFor={inputId} disabled={disabled}>
            {label}
          </InputLabelStyle>
        </LabelRow>
      )}
      <SliderRow ref={sliderRef} onPointerDown={handlePointerDown}>
        <HiddenInput
          ref={inputRef}
          id={inputId}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={handleInputChange}
          onBlur={() => onChangeCommitted?.(value)}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-label={label ?? 'Slider'}
        />
        <Track />
        <Fill widthPercent={percent} />
        <Thumb leftPercent={percent} disabled={disabled} />
      </SliderRow>
      <MinMaxRow>
        <MinMaxLabel>{min} €</MinMaxLabel>
        <MinMaxLabel>{max} €</MinMaxLabel>
      </MinMaxRow>
      {error ? <ValidationErrorStyle>{error}</ValidationErrorStyle> : null}
    </SliderWrapper>
  );
};
